import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import './AdminConsolePage.css';

interface User {
  email: string;
  name: string;
  role: string;
  is_approved: boolean;
  is_active: boolean;
  created_at: string;
  approved_at?: string;
  approved_by?: string;
}

interface BulkImportResult {
  summary: { created: number; skipped: number; failed: number; total_rows: number };
  created: { row: number; email: string; name: string; role: string }[];
  skipped: { row: number; email: string; reason: string }[];
  failed: { row: number; email: string; reason: string }[];
}

const AVAILABLE_ROLES = ['Admin', 'QA_Engineer', 'Viewer'];

export const AdminConsolePage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'add_user' | 'all' | 'roles' | 'sessions' | 'dashboard_settings'>('add_user');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('Viewer');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Add User form state
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('Viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CSV bulk import state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<BulkImportResult | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Session tracker state
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionSummary, setSessionSummary] = useState<{today_logins: number; unique_this_week: number; active_now: number} | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionDays, setSessionDays] = useState(30);
  const [sessionUserFilter, setSessionUserFilter] = useState('');

  // Dashboard settings state (AI "time saved" estimation constants)
  const [dashSettings, setDashSettings] = useState<{ avg_manual_scenario_min: number; avg_manual_testcase_min: number; avg_ai_review_min: number } | null>(null);
  const [dashSettingsLoading, setDashSettingsLoading] = useState(false);
  const [dashSettingsSaving, setDashSettingsSaving] = useState(false);

  const loadDashboardSettings = async () => {
    setDashSettingsLoading(true);
    try {
      const resp = await apiClient.get('/api/v1/admin/dashboard-settings');
      setDashSettings(resp.data as any);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load dashboard settings');
    } finally {
      setDashSettingsLoading(false);
    }
  };

  const handleSaveDashboardSettings = async () => {
    if (!dashSettings) return;
    setDashSettingsSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const resp = await apiClient.put('/api/v1/admin/dashboard-settings', dashSettings);
      setDashSettings(resp.data as any);
      setSuccessMessage('Dashboard settings saved');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save dashboard settings');
    } finally {
      setDashSettingsSaving(false);
    }
  };

  // Load the user list once on mount (independent of which tab is active) so
  // the "All Users (N)" tab label shows the real count immediately instead of
  // "(0)" until the tab is clicked for the first time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadUsers();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'all') loadUsers();
    if (activeTab === 'sessions') loadSessions();
    if (activeTab === 'dashboard_settings') loadDashboardSettings();
  }, [activeTab]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'sessions') loadSessions();
  }, [sessionDays, sessionUserFilter]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const users = await apiClient.getAllUsers();
      setAllUsers(users);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const data = await apiClient.getUserSessions(sessionDays, sessionUserFilter || undefined);
      setSessions(data.sessions || []);
      setSessionSummary(data.summary || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      await apiClient.createUser({ name: newUserName, email: newUserEmail, role: newUserRole });
      setSuccessMessage(`User "${newUserName}" added successfully with role "${newUserRole}". They can now log in using "Continue with Google" with their ${newUserEmail} account.`);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserRole('Viewer');
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setError('');
    setSuccessMessage('');
    setCsvResult(null);
    try {
      const result = await apiClient.bulkImportUsers(csvFile);
      setCsvResult(result);
      if (result.summary.created > 0) {
        setSuccessMessage(`Bulk import complete: ${result.summary.created} user(s) created.`);
      }
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to import users');
    } finally {
      setCsvUploading(false);
    }
  };

  const handleDeactivate = async (userEmail: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      setError(''); setSuccessMessage('');
      await apiClient.deactivateUser(userEmail);
      setSuccessMessage('User deactivated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (userEmail: string) => {
    try {
      setError(''); setSuccessMessage('');
      await apiClient.activateUser(userEmail);
      setSuccessMessage('User activated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to activate user');
    }
  };

  const handleChangeRole = async (userEmail: string, newRole: string) => {
    try {
      setError(''); setSuccessMessage('');
      await apiClient.updateUserRole(userEmail, newRole);
      setSuccessMessage('Role updated successfully');
      setEditingRole(null);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const filteredUsers = useMemo(() => {
    const q = userSearchQuery.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  }, [allUsers, userSearchQuery]);

  if (currentUser?.role !== 'Admin') {
    return (
      <div className="admin-console-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin console.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-console-page">
      <div className="admin-header">
        <h1>Admin Console</h1>
        <p>Manage user accounts and permissions</p>
      </div>

      <div className="admin-tabs">
        <button className={`tab ${activeTab === 'add_user' ? 'active' : ''}`} onClick={() => setActiveTab('add_user')}>
          Add User
        </button>
        <button className={`tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All Users ({allUsers.length})
        </button>
        <button className={`tab ${activeTab === 'roles' ? 'active' : ''}`} onClick={() => setActiveTab('roles')}>
          Role Management
        </button>
        <button className={`tab ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>
          Session Tracker
        </button>
        <button className={`tab ${activeTab === 'dashboard_settings' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard_settings')}>
          Dashboard Settings
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {isLoading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="users-container">

          {/* ── Add User Tab ── */}
          {activeTab === 'add_user' && (
            <div className="add-user-section">
              <h3>Add New User</h3>
              <p className="add-user-hint">Enter the user's details below. They will be able to log in using <strong>Continue with Google</strong> with the email address you provide — no password needed.</p>
              <form className="add-user-form" onSubmit={handleAddUser}>
                <div className="form-group">
                  <label htmlFor="new-user-name">Full Name <span className="required">*</span></label>
                  <input
                    id="new-user-name"
                    type="text"
                    className="form-control"
                    placeholder="e.g. John Doe"
                    value={newUserName}
                    onChange={e => setNewUserName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-user-email">Email Address <span className="required">*</span></label>
                  <input
                    id="new-user-email"
                    type="email"
                    className="form-control"
                    placeholder="e.g. john@company.com"
                    value={newUserEmail}
                    onChange={e => setNewUserEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-user-role">Role <span className="required">*</span></label>
                  <select
                    id="new-user-role"
                    className="form-control"
                    value={newUserRole}
                    onChange={e => setNewUserRole(e.target.value)}
                  >
                    {AVAILABLE_ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <p className="role-hint">
                    {newUserRole === 'Admin' && 'Full system access including user management.'}
                    {newUserRole === 'QA_Engineer' && 'Can create, edit, and execute tests.'}
                    {newUserRole === 'Viewer' && 'Read-only access to view tests and results.'}
                  </p>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>

              {/* ── CSV Bulk Import ── */}
              <div className="bulk-import-divider">
                <span>or</span>
              </div>

              <div className="bulk-import-section">
                <div className="bulk-import-header">
                  <h4>Bulk Import via CSV</h4>
                  <button
                    className="btn btn-link-sm"
                    onClick={() => apiClient.downloadBulkImportTemplate()}
                    title="Download CSV template"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Template
                  </button>
                </div>
                <p className="bulk-import-hint">
                  Upload a CSV with columns: <code>name</code>, <code>email</code>, <code>role</code>.<br/>
                  Valid roles: <code>Admin</code>, <code>QA_Engineer</code>, <code>Viewer</code>. Existing emails are skipped.
                </p>
                <div className="bulk-import-controls">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv"
                    className="bulk-import-file-input"
                    onChange={e => { setCsvFile(e.target.files?.[0] || null); setCsvResult(null); }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleCsvUpload}
                    disabled={!csvFile || csvUploading}
                  >
                    {csvUploading ? 'Importing...' : 'Import Users'}
                  </button>
                </div>

                {/* Import result summary */}
                {csvResult && (
                  <div className="bulk-import-result">
                    <div className="bulk-result-summary">
                      <span className="bulk-stat bulk-stat-created">✅ {csvResult.summary.created} created</span>
                      <span className="bulk-stat bulk-stat-skipped">⏭ {csvResult.summary.skipped} skipped</span>
                      <span className="bulk-stat bulk-stat-failed">❌ {csvResult.summary.failed} failed</span>
                    </div>

                    {csvResult.created.length > 0 && (
                      <details className="bulk-result-details" open>
                        <summary>Created ({csvResult.created.length})</summary>
                        <table className="bulk-result-table">
                          <thead><tr><th>Row</th><th>Name</th><th>Email</th><th>Role</th></tr></thead>
                          <tbody>
                            {csvResult.created.map((r, i) => (
                              <tr key={i}><td>{r.row}</td><td>{r.name}</td><td>{r.email}</td><td>{r.role}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    )}

                    {csvResult.skipped.length > 0 && (
                      <details className="bulk-result-details">
                        <summary>Skipped ({csvResult.skipped.length})</summary>
                        <table className="bulk-result-table">
                          <thead><tr><th>Row</th><th>Email</th><th>Reason</th></tr></thead>
                          <tbody>
                            {csvResult.skipped.map((r, i) => (
                              <tr key={i}><td>{r.row}</td><td>{r.email}</td><td className="bulk-reason-skip">{r.reason}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    )}

                    {csvResult.failed.length > 0 && (
                      <details className="bulk-result-details">
                        <summary>Failed ({csvResult.failed.length})</summary>
                        <table className="bulk-result-table">
                          <thead><tr><th>Row</th><th>Email</th><th>Reason</th></tr></thead>
                          <tbody>
                            {csvResult.failed.map((r, i) => (
                              <tr key={i}><td>{r.row}</td><td>{r.email}</td><td className="bulk-reason-fail">{r.reason}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── All Users Tab ── */}
          {activeTab === 'all' && (
            <div className="all-users">
              <div className="user-search-wrap">
                <svg className="user-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="user-search-input"
                  placeholder="Search by name, email, or role..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                />
                {userSearchQuery && (
                  <button className="user-search-clear" onClick={() => setUserSearchQuery('')} title="Clear search">×</button>
                )}
              </div>

              {allUsers.length === 0 ? (
                <div className="empty-state"><p>No users found</p></div>
              ) : filteredUsers.length === 0 ? (
                <div className="empty-state"><p>No users match "{userSearchQuery}"</p></div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.email}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            {editingRole === user.email ? (
                              <div className="role-edit">
                                <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="role-select">
                                  {AVAILABLE_ROLES.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                                <button className="btn btn-sm btn-success" onClick={() => handleChangeRole(user.email, selectedRole)}>Save</button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingRole(null)}>Cancel</button>
                              </div>
                            ) : (
                              <div className="role-display">
                                <span className={`role-badge ${user.role}`}>{user.role}</span>
                                {user.email !== currentUser?.email && (
                                  <button className="btn-icon" onClick={() => { setEditingRole(user.email); setSelectedRole(user.role); }} title="Change role">✏️</button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {!user.is_approved && <span className="status-badge pending">Pending</span>}
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>
                            {user.email !== currentUser?.email && (
                              <div className="action-buttons">
                                {user.is_active ? (
                                  <button className="btn btn-sm btn-warning" onClick={() => handleDeactivate(user.email)}>Deactivate</button>
                                ) : (
                                  <button className="btn btn-sm btn-success" onClick={() => handleActivate(user.email)}>Activate</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Role Management Tab ── */}
          {activeTab === 'roles' && (
            <div className="role-management">
              <div className="roles-info">
                <h3>Available Roles</h3>
                <div className="roles-grid">
                  {AVAILABLE_ROLES.map(role => (
                    <div key={role} className="role-card">
                      <h4>{role}</h4>
                      <p className="role-description">
                        {role === 'Admin' && 'Full system access including user management'}
                        {role === 'QA_Engineer' && 'Can create and execute tests'}
                        {role === 'Viewer' && 'Read-only access to view tests and results'}
                      </p>
                      <div className="role-permissions">
                        <strong>Permissions:</strong>
                        <ul>
                          {role === 'Admin' && (<><li>✓ Manage users and roles</li><li>✓ Create/edit/delete projects</li><li>✓ Create/edit/delete scenarios</li><li>✓ Create/edit/delete test cases</li><li>✓ Execute tests</li><li>✓ Export and import</li></>)}
                          {role === 'QA_Engineer' && (<><li>✓ Create/edit/delete scenarios</li><li>✓ Create/edit/delete test cases</li><li>✓ Execute tests</li><li>✓ Export and import</li><li>✓ View projects</li></>)}
                          {role === 'Viewer' && (<><li>✓ View projects</li><li>✓ View scenarios</li><li>✓ View test cases</li><li>✓ View test results</li></>)}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Session Tracker Tab ── */}
          {activeTab === 'sessions' && (
            <div className="session-tracker">
              {/* Controls */}
              <div className="session-controls">
                <div className="session-filter-group">
                  <label>Date Range</label>
                  <select value={sessionDays} onChange={e => setSessionDays(Number(e.target.value))} className="form-control" style={{width: 'auto'}}>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last year</option>
                  </select>
                </div>
                <div className="session-filter-group">
                  <label>User</label>
                  <select value={sessionUserFilter} onChange={e => setSessionUserFilter(e.target.value)} className="form-control" style={{width: 'auto'}}>
                    <option value="">All Users</option>
                    {Array.from(new Set(sessions.map(s => s.email))).sort().map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
                <button className="btn btn-secondary" onClick={loadSessions} disabled={sessionsLoading}>
                  {sessionsLoading ? 'Loading...' : '↻ Refresh'}
                </button>
              </div>

              {/* Summary Cards */}
              {sessionSummary && (
                <div className="session-summary-cards">
                  <div className="session-summary-card">
                    <div className="session-summary-num">{sessionSummary.today_logins}</div>
                    <div className="session-summary-lbl">Today's Logins</div>
                  </div>
                  <div className="session-summary-card session-summary-active">
                    <div className="session-summary-num" style={{color: '#16a34a'}}>{sessionSummary.active_now}</div>
                    <div className="session-summary-lbl">Active (last 30 min)</div>
                  </div>
                  <div className="session-summary-card">
                    <div className="session-summary-num">{sessionSummary.unique_this_week}</div>
                    <div className="session-summary-lbl">Unique Users This Week</div>
                  </div>
                  <div className="session-summary-card">
                    <div className="session-summary-num">{sessions.length}</div>
                    <div className="session-summary-lbl">Total Sessions Shown</div>
                  </div>
                </div>
              )}

              {/* Timeline Table */}
              {sessionsLoading ? (
                <div className="loading">Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <div className="empty-state"><p>No login sessions recorded yet.</p></div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Login Time</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s, i) => (
                        <tr key={i}>
                          <td>
                            <div style={{fontWeight: 600}}>{s.name || s.email.split('@')[0]}</div>
                            <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>{s.email}</div>
                          </td>
                          <td><span className={`role-badge ${s.role}`}>{s.role}</span></td>
                          <td style={{fontSize: '0.82rem', color: '#374151'}}>
                            {s.logged_in_at ? new Date(s.logged_in_at).toLocaleString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : '—'}
                          </td>
                          <td>
                            {s.is_recent ? (
                              <span style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', fontWeight: 700, color: '#16a34a'}}>
                                <span style={{width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block'}}/>
                                Active Now
                              </span>
                            ) : (
                              <span style={{fontSize: '0.75rem', color: '#9ca3af'}}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Dashboard Settings Tab ── */}
          {activeTab === 'dashboard_settings' && (
            <div className="add-user-section">
              <h3>AI Time-Saved Estimation Constants</h3>
              <p className="add-user-hint">
                The Dashboard's "AI Time Saved" metric is an <strong>estimate</strong>, not a measurement —
                there's no record of how long a scenario or test case actually took to write. It's calculated as
                (AI-generated count × avg. manual creation time) minus (count × avg. AI-review time), using the
                constants below. Adjust them to match your team's real pace.
              </p>
              {dashSettingsLoading || !dashSettings ? (
                <div className="loading">Loading settings...</div>
              ) : (
                <form className="add-user-form" onSubmit={(e) => { e.preventDefault(); handleSaveDashboardSettings(); }}>
                  <div className="form-group">
                    <label className="form-label-with-tip">
                      Avg. manual scenario creation time (minutes)
                      <span
                        className="field-info-icon"
                        title="Creation time includes thinking through the scenario, identifying the areas it impacts, writing it, and checking/verifying it against the design."
                      >ⓘ</span>
                    </label>
                    <input
                      type="number" min={1} step={1} className="form-control"
                      value={dashSettings.avg_manual_scenario_min}
                      onChange={e => setDashSettings({ ...dashSettings, avg_manual_scenario_min: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label-with-tip">
                      Avg. manual test case creation time (minutes)
                      <span
                        className="field-info-icon"
                        title="Creation time includes thinking through the test case, identifying the areas it impacts, writing it, and checking/verifying it against the design."
                      >ⓘ</span>
                    </label>
                    <input
                      type="number" min={1} step={1} className="form-control"
                      value={dashSettings.avg_manual_testcase_min}
                      onChange={e => setDashSettings({ ...dashSettings, avg_manual_testcase_min: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label-with-tip">
                      Review Scenarios (minutes)
                      <span
                        className="field-info-icon"
                        title="Time an AI-generated scenario/test case saves by not needing this manual review step."
                      >ⓘ</span>
                    </label>
                    <input
                      type="number" min={0} step={1} className="form-control"
                      value={dashSettings.avg_ai_review_min}
                      onChange={e => setDashSettings({ ...dashSettings, avg_ai_review_min: Number(e.target.value) })}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={dashSettingsSaving}>
                    {dashSettingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </form>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

